<template>
  <div class="glass-panel rounded-2xl overflow-hidden flex flex-col" :class="theme.ring">

    <!-- Cabecera -->
    <div class="flex items-center gap-3.5 px-5 py-4 border-b" :class="theme.headerBorder">
      <div class="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" :class="theme.iconBg">
        <i class="mdi text-lg text-white" :class="icon"></i>
      </div>
      <div class="min-w-0">
        <h2 class="text-[0.9rem] font-bold text-slate-900 dark:text-white m-0 truncate">{{ title }}</h2>
        <p class="text-[0.72rem] text-slate-500 dark:text-slate-400 mt-0.5 mb-0 truncate">{{ description }}</p>
      </div>
    </div>

    <!-- Cuerpo -->
    <div class="px-5 py-5 flex flex-col gap-3.5 flex-1">
      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1">
          <span class="text-[0.62rem] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Desde</span>
          <div class="relative">
            <i class="mdi mdi-calendar-outline absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[0.9rem] pointer-events-none"></i>
            <input v-model="from" type="date"
              class="w-full rounded-lg border border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-white/5 pl-8 pr-2.5 py-2 text-[0.75rem] text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:bg-white dark:focus:bg-white/10 transition-all"
              :class="theme.focusRing" />
          </div>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[0.62rem] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Hasta</span>
          <div class="relative">
            <i class="mdi mdi-calendar-outline absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[0.9rem] pointer-events-none"></i>
            <input v-model="to" type="date"
              class="w-full rounded-lg border border-slate-200/70 dark:border-white/10 bg-white/50 dark:bg-white/5 pl-8 pr-2.5 py-2 text-[0.75rem] text-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:bg-white dark:focus:bg-white/10 transition-all"
              :class="theme.focusRing" />
          </div>
        </label>
      </div>

      <p v-if="errorMessage" class="flex items-center gap-1.5 text-[0.72rem] text-red-500 dark:text-red-400 m-0">
        <i class="mdi mdi-alert-circle-outline text-sm"></i>
        {{ errorMessage }}
      </p>

      <div class="flex-1"></div>

      <button type="button" :disabled="loading" @click="handleDownload"
        class="flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[0.8rem] font-semibold text-white transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
        :class="theme.buttonBg">
        <i class="mdi text-base" :class="loading ? 'mdi-loading animate-spin' : 'mdi-file-excel-outline'"></i>
        {{ loading ? 'Generando reporte...' : 'Descargar reporte' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuthStore } from '@/features/auth/stores/AuthStore'
import { downloadBlob, extractFilenameFromDisposition, extractBlobErrorMessage } from '../utils/downloadFile'

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, required: true },
  icon: { type: String, required: true },
  // 'approved' | 'disapproved' — controla los colores de la card (verde/rojo).
  variant: { type: String, required: true },
  // (plantId, { from, to }) => Promise<AxiosResponse> con responseType: 'blob'
  downloadFn: { type: Function, required: true },
  filenamePrefix: { type: String, required: true },
})

// Paletas completas y literales (no interpoladas) para que Tailwind las
// genere sin problema.
const THEMES = {
  approved: {
    ring: 'ring-1 ring-emerald-200/50 dark:ring-emerald-500/15',
    headerBorder: 'border-emerald-100/70 dark:border-emerald-500/15',
    iconBg: 'bg-emerald-500',
    focusRing: 'focus:ring-emerald-500/25 focus:border-emerald-400 dark:focus:border-emerald-500/50',
    buttonBg: 'bg-emerald-600 hover:bg-emerald-700',
  },
  disapproved: {
    ring: 'ring-1 ring-red-200/50 dark:ring-red-500/15',
    headerBorder: 'border-red-100/70 dark:border-red-500/15',
    iconBg: 'bg-red-500',
    focusRing: 'focus:ring-red-500/25 focus:border-red-400 dark:focus:border-red-500/50',
    buttonBg: 'bg-red-600 hover:bg-red-700',
  },
}

const theme = THEMES[props.variant]

const authStore = useAuthStore()

const toInputDate = (date) => date.toISOString().slice(0, 10)
const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const from = ref(toInputDate(firstDayOfMonth))
const to = ref(toInputDate(today))
const loading = ref(false)
const errorMessage = ref('')

async function handleDownload() {
  errorMessage.value = ''

  if (!from.value || !to.value) {
    errorMessage.value = 'Selecciona ambas fechas.'
    return
  }
  if (from.value > to.value) {
    errorMessage.value = 'La fecha inicial no puede ser mayor que la fecha final.'
    return
  }
  if (!authStore.plantId) {
    errorMessage.value = 'No se encontró la planta asociada a tu sesión.'
    return
  }

  loading.value = true
  try {
    const response = await props.downloadFn(authStore.plantId, { from: from.value, to: to.value })

    const blob = new Blob([response.data], {
      type: response.headers?.['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const filename =
      extractFilenameFromDisposition(response.headers?.['content-disposition']) ||
      `${props.filenamePrefix}-${from.value}_a_${to.value}.xlsx`

    downloadBlob(blob, filename)
  } catch (error) {
    console.error(`Error al generar el reporte de ${props.filenamePrefix}:`, error)
    errorMessage.value = await extractBlobErrorMessage(error)
  } finally {
    loading.value = false
  }
}
</script>
